/**
 * Shift mode - the ATC loop for mission games: the scene's problems keep coming back during a shift
 * and the player keeps putting them right. Pure functions over the same spec and context as
 * simEngine.js; nothing here changes how a single action works.
 *
 * A mission's goal is split into PROBLEM GROUPS: the right actions, items and goal conditions that
 * belong to one problem (the box stack + Camera C7 + "log the camera"). Groups are found from the data
 * alone (so missions built before shift mode work too):
 *   - an action is joined to what it acts on (a write: what the entry is about), its effects and its
 *     `done` prerequisites;
 *   - "hubs" join nothing: the places things are taken to (key cabinet, filing cabinet) and records
 *     written in (patrol log) - otherwise every problem that uses the log would merge into one;
 *   - a requirement on another item joins only when it points at ONE other group. An action that needs
 *     several groups put right first (arm the alarm last) is a CLOSING action: it isn't repeated, and it
 *     waits until the shift is over.
 * A group can repeat when it has its own actions and goal conditions, starts out wrong, and can be put
 * right from the start using only its own actions.
 */
import { SHOWN_PROPS } from './mechanics/engine';
import { canStillWin, evaluate } from './simEngine';

const clone = (v) => JSON.parse(JSON.stringify(v));
const conds = (goal) => (!goal ? [] : goal.all ? goal.all.flatMap(conds) : [goal]);
/** Items a condition reads (compare conditions, at any depth). */
const condItems = (c) => (!c ? [] : c.all ? c.all.flatMap(condItems) : c.any ? c.any.flatMap(condItems)
  : c.not ? condItems(c.not) : c.item ? [c.item] : []);
const condDones = (c) => (!c ? [] : c.all ? c.all.flatMap(condDones) : c.any ? c.any.flatMap(condDones)
  : c.not ? condDones(c.not) : c.done !== undefined ? [c.done] : []);

function unionFind() {
  const parent = new Map();
  const find = (x) => {
    if (!parent.has(x)) parent.set(x, x);
    let r = x;
    while (parent.get(r) !== r) r = parent.get(r);
    parent.set(x, r);
    return r;
  };
  return { find, union: (a, b) => parent.set(find(a), find(b)) };
}

export function problemGroups(spec) {
  const itemIds = new Set(spec.items.map((it) => it.id));
  const kindOf = Object.fromEntries(spec.items.map((it) => [it.id, it.kind]));
  const work = spec.actions.filter((a) => a.kind === 'right' && a.interaction !== 'exit');
  const hubs = new Set(spec.actions.flatMap((a) => [
    a.drop_on && itemIds.has(a.drop_on) ? a.drop_on : null,
    a.interaction === 'write' ? a.item : null
  ]).filter(Boolean));
  const anchorOf = (a) => (a.interaction === 'write' ? (a.about && a.about !== a.item ? a.about : null) : a.item);
  const A = (id) => `a:${id}`;
  const I = (id) => `i:${id}`;
  const uf = unionFind();

  // Pass 1: what each action acts on, changes, and comes after.
  work.forEach((a) => {
    uf.find(A(a.id));
    const anchor = anchorOf(a);
    if (anchor && itemIds.has(anchor)) uf.union(A(a.id), I(anchor));
    (a.effects || []).forEach((e) => {
      if (itemIds.has(e.item) && (!hubs.has(e.item) || e.item === anchor)) uf.union(A(a.id), I(e.item));
    });
    condDones(a.requires).forEach((d) => { if (work.some((w) => w.id === d)) uf.union(A(a.id), A(d)); });
  });
  // Pass 2: requirements on other items - one other group joins, several make a closing action.
  const closing = new Set();
  const joins = work.map((a) => {
    const others = new Set(condItems(a.requires).filter((id) => itemIds.has(id) && !hubs.has(id)).map((id) => uf.find(I(id))));
    others.delete(uf.find(A(a.id)));
    if (others.size > 1) closing.add(a.id);
    return [a, others];
  });
  joins.forEach(([a, others]) => { if (!closing.has(a.id)) others.forEach((root) => uf.union(A(a.id), root)); });
  // A closing action's own goal conditions stay with it (they're not repeated).
  closing.forEach((id) => uf.union(A(id), `closing:${id}`));

  const groups = new Map();
  const groupFor = (node) => {
    const root = uf.find(node);
    if (!groups.has(root)) groups.set(root, { root, actions: [], items: new Set(), goal: [], closing: false });
    return groups.get(root);
  };
  work.forEach((a) => {
    const g = groupFor(A(a.id));
    g.actions.push(a.id);
    if (closing.has(a.id)) g.closing = true;
  });
  spec.items.forEach((it) => { const root = uf.find(I(it.id)); if (groups.has(root)) groups.get(root).items.add(it.id); });
  const unplaced = [];
  conds(spec.goal).forEach((c) => {
    const node = c.done !== undefined ? A(c.done) : c.item ? I(c.item) : null;
    const g = node && groups.get(uf.find(node));
    if (g) g.goal.push(c); else unplaced.push(c);
  });

  // An event that reads several groups and changes this one (it goes off when armed too early) also
  // makes it a closing step.
  (spec.events || []).forEach((ev) => {
    const read = new Set([...condItems(ev.when).filter((id) => itemIds.has(id) && !hubs.has(id)).map((id) => uf.find(I(id))),
      ...condDones(ev.when).filter((d) => work.some((w) => w.id === d)).map((d) => uf.find(A(d)))]);
    (ev.effects || []).forEach((e) => {
      const g = itemIds.has(e.item) && groups.get(uf.find(I(e.item)));
      if (g && [...read].some((root) => root !== g.root)) g.closing = true;
    });
  });

  const all = [...groups.values()].map((g, n) => {
    const items = [...g.items];
    const traps = spec.actions.filter((a) => a.kind === 'trap' && a.interaction !== 'exit' && items.includes(a.item)).map((a) => a.id);
    // The thing a passer-by brings back: the problem's own person, else the thing that's out of place.
    const person = items.find((id) => kindOf[id] === 'person');
    const anchor = [...g.goal.flatMap(condItems), ...g.actions.map((id) => anchorOf(spec.actions.find((a) => a.id === id)))]
      .find((id) => id && itemIds.has(id) && kindOf[id] !== 'record') || items[0] || null;
    return { id: `g${n + 1}`, actions: g.actions, traps, items, goal: g.goal, closing: g.closing, person: person || null, anchor };
  });
  const startCtx = { state: Object.fromEntries(spec.items.map((it) => [it.id, clone(it.state || {})])), done: [], seen: [], fired: [] };
  // Steps that serve every problem (open the filing cabinet) have no goal of their own.
  const support = all.filter((g) => !g.closing && g.goal.length === 0).flatMap((g) => g.actions);
  all.forEach((g) => {
    g.support = support;
    // Something visible has to go wrong again (a state, not only "a line was written").
    // Something you can SEE has to go wrong again (a drawn state, not only "a line was written").
    g.repeatable = !g.closing && g.actions.length > 0 && g.items.length > 0 && g.goal.some((c) => c.item && SHOWN_PROPS.has(c.prop))
      && !evaluate({ all: g.goal }, startCtx) && canStillWin(groupSpec(spec, g), startCtx);
  });
  return { groups: all, closingActions: all.filter((g) => g.closing).flatMap((g) => g.actions), unplaced };
}

/** The mission cut down to one group: its own actions (and the shared steps) and goal, no mission-wide fail. */
export function groupSpec(spec, group) {
  const own = new Set([...group.actions, ...(group.support || [])]);
  return { ...spec, actions: spec.actions.filter((a) => own.has(a.id)), goal: { all: group.goal }, fail: null };
}

/** Shift mode is offered when at least one problem can repeat and every goal condition belongs to a group. */
export function shiftPlan(spec) {
  const { groups, closingActions, unplaced } = problemGroups(spec);
  const repeat = groups.filter((g) => g.repeatable);
  const available = repeat.length > 0 && unplaced.length === 0
    && groups.every((g) => g.repeatable || g.closing || g.goal.length === 0);
  return { available, groups: repeat, closingActions };
}

export const groupSolved = (group, ctx) => evaluate({ all: group.goal }, ctx);

/** A wrong order inside this problem has left it impossible to put right (e.g. fixed before logging). */
export const groupLost = (spec, group, ctx) => !groupSolved(group, ctx) && !canStillWin(groupSpec(spec, group), ctx);

/** The problem happens again: its items go back to how the mission started, its actions can be done
 * again and its events can fire again. Records keep their rows - the log grows over the shift. */
export function rearm(spec, group, ctx) {
  const initial = Object.fromEntries(spec.items.map((it) => [it.id, it.state || {}]));
  const redo = new Set([...group.actions, ...group.traps]);
  const touched = (ev) => (ev.effects || []).some((e) => group.items.includes(e.item));
  const events = new Set((spec.events || []).filter(touched).map((ev) => ev.id));
  const state = { ...ctx.state };
  group.items.forEach((id) => {
    const keep = Array.isArray(state[id]?.entries) ? { entries: state[id].entries } : {};
    state[id] = { ...clone(initial[id] || {}), ...keep };
  });
  return { ...ctx, state, done: ctx.done.filter((id) => !redo.has(id)), fired: ctx.fired.filter((id) => !events.has(id)) };
}

/** A slip (a trap done during a shift) is counted, then taken back so the shift goes on. */
export const forgiveSlip = (ctx, actionId) => ({ ...ctx, done: ctx.done.filter((id) => id !== actionId) });

/** Shift pacing, ATC-style: more problems open at once as the shift goes on, and less time for each. */
export const SHIFT_SECONDS = 180;
export function pace(progress, groupCount) {
  return {
    maxOpen: Math.max(1, Math.min(groupCount, 1 + Math.floor(progress * groupCount * 1.2))),
    gapSeconds: Math.round(12 - 7 * progress),        // quiet time before the next problem
    deadlineScale: 1 - 0.35 * progress
  };
}
export const deadlineSeconds = (group, scale = 1) => Math.round((22 + 9 * group.actions.length) * scale);

/** H:MM shift clock: real seconds are game minutes, starting at the first time the scene mentions. */
export function startMinutes(lines) {
  const m = (lines || []).join(' ').match(/\b([01]?\d|2[0-3])[:.]([0-5]\d)\b/);
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
}
export const clockText = (minutes) => {
  const t = ((Math.floor(minutes) % 1440) + 1440) % 1440;
  return `${String(Math.floor(t / 60)).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`;
};
